// Published-content events, in two flavours:
//
//   usePublishedEvents()          the /events index — every published event
//                                 that clears the structural minimum below.
//   useFeaturedPublishedEvents()  the home page's "Campus Events" rail
//                                 (components/sections/Events.tsx) — the
//                                 same list, filtered to webadmin's own
//                                 `featured` records.
//
// Both require only the bare minimum a card can't be built without — a
// title and at least one session with a start time — since there is no
// content to fall back to (not a judgement call, a hard requirement of
// EventItem's shape). Earlier versions of this file also excluded a record
// when its title equalled its description, or when a session's start and
// end were the same instant (both true of the one record published so
// far, 2026-09-18's "SIH") — removed by request so /events and the home
// rail apply the same bar. `timeRangeOrInstant` below still avoids
// printing a nonsensical "6:16 PM – 6:16 PM" for that kind of
// zero-duration record — showing one honest instant instead of a fake
// range is a formatting fix, not a content judgement, so it stays either
// way.
//
// An event is `upcoming` or `past` by whether its session's own start time is
// still ahead of now, computed below.
//
// Description: webadmin has one text field for it, so `summary` (the card text)
// is that whole description, and the detail page shows it in the body — built
// by eventDescription.ts — rather than the banner; `summaryIsDescription` tells
// the page which. An event with no description falls back to its title for the
// card and shows nothing in the body.
//
// Photos: process-content-images.mjs now reprocesses `banner` for this
// collection too (sites/biet/site-content/events/<slug>.<ext>), the same
// discipline as everywhere else — asMedia() below only ever accepts an
// already-reprocessed bare key, never banner's raw webadmin URL, so an
// event whose banner hasn't been reprocessed yet still falls back to the
// text placeholder (`hero`) rather than bypassing the transform CDN.
// `images[]`/`cover` are still unused — banner is the one clearly-scoped
// "this event's own photo" field; images[] has no structured role data,
// same reasoning as the department images[] array.
import type { Media, SiteEvent } from "@zivin-labs/site-content";
import { useCollection } from "./siteContent";
import { resolveDepartment } from "./departments";
import { eventTimestamp, type EventItem } from "./events";
import { descriptionBlocks } from "./eventDescription";

/** Same bare-key discipline as publishedDepartmentExtras.ts's asMedia — never route an unprocessed webadmin URL through the transform CDN. */
function asMedia(ref: { url?: string; alt?: string; width?: number; height?: number } | null | undefined, fallbackAlt: string): Media | undefined {
  if (!ref?.url || ref.url.includes("://") || !ref.width || !ref.height) return undefined;
  return { url: ref.url, alt: ref.alt || fallbackAlt, width: ref.width, height: ref.height };
}

const EVENT_TYPE_LABEL: Record<string, string> = {
  hackathon: "Hackathon",
  workshop: "Workshop",
  seminar: "Talk",
  conference: "Talk",
  cultural: "Cultural",
  fest: "Cultural",
};

/** Falls back to Title Case of the raw eventType for a value not in the map above, so a new webadmin category never renders blank. */
function eventTag(eventType: string | undefined): string {
  if (!eventType?.trim()) return "Event";
  return (
    EVENT_TYPE_LABEL[eventType] ??
    eventType.replace(/_/g, " ").replace(/\w\S*/g, (w) => w[0].toUpperCase() + w.slice(1).toLowerCase())
  );
}

const IST_DATE = new Intl.DateTimeFormat("en-US", {
  timeZone: "Asia/Kolkata",
  day: "numeric",
  month: "short",
  year: "numeric",
});
const IST_TIME = new Intl.DateTimeFormat("en-US", {
  timeZone: "Asia/Kolkata",
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
});

function dateBigFromIso(iso: string): EventItem["dateBig"] {
  const parts = IST_DATE.formatToParts(new Date(iso));
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return { day: get("day"), month: get("month"), year: get("year") };
}

function dateLineFromIso(iso: string): string {
  const { day, month, year } = dateBigFromIso(iso);
  return `${day} ${month} ${year}`;
}

/** A real end time prints a range; a missing or identical one prints the single instant instead of a fake "X – X" range. */
function timeRangeOrInstant(startIso: string, endIso?: string): string {
  const start = IST_TIME.format(new Date(startIso));
  if (!endIso || endIso === startIso) return start;
  return `${start} – ${IST_TIME.format(new Date(endIso))}`;
}

/** Human-readable, but suffixed with a slice of webadmin's own id so two events with the same title never collide. */
function eventSlug(title: string, rawId: string): string {
  const base = title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "event";
  return `${base}-${rawId.slice(-6).toLowerCase()}`;
}

/** Structural minimum only — a title and a start time are all a card can't be built without. Returns null otherwise. */
function mapEvent(rec: SiteEvent, now: number): EventItem | null {
  const title = rec.title?.trim();
  const session = rec.sessions?.[0];
  if (!title || !session?.startsAt) return null;

  const description = rec.description?.trim() || title;
  const dept = resolveDepartment(rec.department?.code);
  const validSessions = rec.sessions.filter((s) => s.startsAt);

  return {
    slug: eventSlug(title, rec.slug),
    tag: eventTag(rec.eventType),
    title,
    dateLine: dateLineFromIso(session.startsAt),
    dateBig: dateBigFromIso(session.startsAt),
    time: timeRangeOrInstant(session.startsAt, session.endsAt),
    venue: session.venue?.trim() || "Venue to be announced",
    hero: `${eventTag(rec.eventType).toUpperCase()} · ${title.toUpperCase()}`,
    media: asMedia(rec.banner, title),
    summary: description,
    summaryIsDescription: true,
    body: descriptionBlocks(rec.description),
    schedule:
      validSessions.length > 1
        ? validSessions.map((s) => ({
            time: timeRangeOrInstant(s.startsAt, s.endsAt),
            what: s.title?.trim() || title,
            who: s.speaker?.trim(),
          }))
        : undefined,
    registrationDeadline: rec.registration?.closesAt ? dateLineFromIso(rec.registration.closesAt) : undefined,
    registrationUrl: rec.externalUrl?.includes("://") ? rec.externalUrl : undefined,
    departmentCode: dept?.code,
    featured: rec.featured === true,
    status: new Date(session.startsAt).getTime() > now ? "upcoming" : "past",
    source: "Published by the department via BIET's content platform",
  };
}

/** Every published event, and whether the content is still arriving — for a page that must tell "not found" from "not loaded yet". */
export function usePublishedEventsState(): { events: EventItem[]; loading: boolean } {
  const { items, loading } = useCollection("events");
  const now = Date.now();
  const events: EventItem[] = [];

  for (const rec of items as SiteEvent[]) {
    const mapped = mapEvent(rec, now);
    if (mapped) events.push(mapped);
  }

  return { events, loading };
}

/** The /events index — every published event that clears the structural minimum (see the module comment above); no content-quality guard. */
export function usePublishedEvents(): EventItem[] {
  return usePublishedEventsState().events;
}

/** The home page's "Campus Events" rail — webadmin's own `featured` records only, from the same unguarded list above, newest first. */
export function useFeaturedPublishedEvents(): EventItem[] {
  return usePublishedEvents()
    .filter((e) => e.featured)
    .sort((a, b) => eventTimestamp(b) - eventTimestamp(a));
}

/** A department's own published events, newest first — matched on the published department's code, which must resolve to this department. */
export function useDepartmentEvents(departmentCode: string | undefined): EventItem[] {
  const all = usePublishedEvents();
  if (!departmentCode) return [];
  return all
    .filter((e) => e.departmentCode === departmentCode)
    .sort((a, b) => eventTimestamp(b) - eventTimestamp(a));
}
